import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  useParams,
  useLocation,
  UNSAFE_NavigationContext
} from 'react-router-dom'

import type { CollapseProps, MenuProps } from 'antd'
import {
  Collapse,
  Dropdown,
  Modal,
  notification,
  Progress,
  Tooltip
} from 'antd'
import styled from 'styled-components'
import _ from 'lodash'
import { colours, sizes } from '@qflow/theme'

import {
  ColourButton,
  icons,
  Spacer,
  Spinner,
  Text
} from 'portal/lib/primitives'
import { trackMixpanel } from 'portal/store/mixpanel/actions'
import {
  copyAlertRulesToProjectIds,
  copyAndSaveAlertRules,
  editAlertRules,
  getAlertUsageByProject
} from 'portal/store/projectAlertRules/actions'

import { AlertRule } from './AlertRule'
import {
  ALERT_RULE_GROUPING,
  ALERT_RULE_GROUPS,
  ALERT_RULE_TITLE_MAPPER,
  DEFAULT_ALERT_PROPS,
  determineAlertRuleCount
} from './AlertRules/constants'
import { AlertSettingsCopyToModal } from './AlertSettingsCopyToModal'
import { useAlertRulesLoader } from './useAlertRulesLoader'
import { useCompaniesLoader } from './useCompaniesLoader'
import { useUnsavedChangesWarning } from 'portal/utils/useUnsavedChangesWarning'

export const AlertSettings = ({
  project,
  onHasUnsavedChanges
}: AlertSettingsProps) => {
  // Use State to store AlertRules
  const [alertRules, setAlertRules] = useState<any>({})
  const [alertRulesLoaded, setAlertRulesLoaded] = useState<any>({})
  const [activeKey, setActiveKey] = useState<string[]>([])
  const [copyToModalOpen, setCopyToModalOpen] = useState(false)
  const [uniqueEmailRecipients, setUniqueEmailRecipients] = useState<string[]>(
    []
  )

  const dispatch = useDispatch()
  const { accountId } = useParams<{
    accountId: string
  }>()
  const currentProjectId = project.projectId

  const { loading, error, reload } = useAlertRulesLoader(currentProjectId)

  const { error: companiesError } = useCompaniesLoader(currentProjectId)

  if (companiesError) {
    console.error('Error loading companies:', companiesError)
  }

  const authState = useSelector((state: any) => state.auth)
  const loadedAlertRules = useSelector((state: any) => state.alertRules)
  const projects = useSelector((state: any) => state.projects.projects)
  const projectUsers = useSelector((state: any) => state.projectUsers.users)
  const usersAccountMappings = useSelector((state: any) => state.users.users)
  const accountDetails = authState.accounts?.find(
    (account: any) => account.accountId === accountId
  )
  const userIsAccountAdmin = accountDetails?.isAdmin === true

  const alertUsageByProject = useSelector((state: any) => {
    return state.alertRules.alertUsageByProject
  })

  const filteredProjects = useMemo(
    () =>
      projects.filter((project: any) => {
        // Filter out the current project id and,
        if (project.projectId === currentProjectId) {
          return false
        }
        // if user is not an Account Admin, filter out the projects they don't have 'Project Admin' (userRoleId = 3) access to
        if (userIsAccountAdmin) {
          return true
        }
        return accountDetails?.projectPermissions.find(
          (projectPermissions: any) => {
            return (
              projectPermissions.projectId === project.projectId &&
              projectPermissions.userRoleId === 3
            )
          }
        )
      }),
    [projects, currentProjectId, userIsAccountAdmin, accountDetails]
  )

  const projectIds = useMemo(
    () => filteredProjects.map((project: any) => project.projectId),
    [filteredProjects]
  )

  useEffect(() => {
    if (accountId && projectIds?.length > 0 && userIsAccountAdmin) {
      dispatch(getAlertUsageByProject(accountId, projectIds))
    }
  }, [accountId, projectIds, dispatch, accountDetails, userIsAccountAdmin])

  const showSuccessNotification = useCallback(() => {
    notification.success({
      message: `Setting successfully updated`,
      duration: 5,
      placement: 'top'
    })
  }, [])

  const showFailureNotification = useCallback(() => {
    notification.error({
      message: `Setting failed to be updated`,
      description:
        "Please check the values and the project's Active alerts limit.",
      duration: 10,
      placement: 'top'
    })
  }, [])

  const copyAlertsFrom = useCallback(
    copyFromProjectId => {
      if (!accountId || !copyFromProjectId) {
        return
      }

      dispatch(
        copyAndSaveAlertRules(
          accountId,
          project.projectId,
          copyFromProjectId,
          reload,
          showSuccessNotification,
          showFailureNotification
        )
      )

      dispatch(
        trackMixpanel('frontend_alerts_copy_from_click', {
          account_id: accountId,
          project_id: project.projectId,
          copied_from_project_id: copyFromProjectId
        })
      )
    },
    [
      accountId,
      project.projectId,
      dispatch,
      reload,
      showSuccessNotification,
      showFailureNotification
    ]
  )

  const copyAlertsTo = useCallback(
    (selectedProjectIds: React.Key[]) => {
      if (!accountId || !selectedProjectIds.length) {
        return
      }

      dispatch(
        copyAlertRulesToProjectIds(
          accountId,
          project.projectId,
          selectedProjectIds as string[],
          projectIds,
          reload,
          showSuccessNotification,
          showFailureNotification
        )
      )

      dispatch(
        trackMixpanel('frontend_alerts_copy_to_click', {
          account_id: accountId,
          project_id: project.projectId,
          copied_to_project_ids: selectedProjectIds
        })
      )
    },
    [
      accountId,
      project.projectId,
      projectIds,
      dispatch,
      reload,
      showSuccessNotification,
      showFailureNotification
    ]
  )

  // Memoize current project user IDs to avoid recalculating for each project
  const currentProjectUserIds = useMemo(() => {
    if (!projectUsers) return []
    return (Object.entries(projectUsers) as [string, ProjectUser][])
      .filter(([, user]) => user.projectPermissions?.length > 0)
      .map(([, user]) => user.userId)
  }, [projectUsers])

  // Memoize user ID set for faster lookups
  const currentProjectUserIdSet = useMemo(
    () => new Set(currentProjectUserIds),
    [currentProjectUserIds]
  )

  useEffect(() => {
    if (
      loadedAlertRules &&
      !loadedAlertRules.loading &&
      !loadedAlertRules.error &&
      loadedAlertRules.data
    ) {
      const clonedData = _.cloneDeep(loadedAlertRules.data)
      setAlertRulesLoaded(clonedData)
      setAlertRules(clonedData)
    }
  }, [loadedAlertRules])

  // Extract unique email recipients when alert rules data changes
  const uniqueEmailRecipientsFromLoaded = useMemo(() => {
    if (!alertRulesLoaded) return []
    const allRecipients = Object.values(
      alertRulesLoaded as Record<string, RuleWithRecipientsType>
    ).flatMap(ruleWithRecipients => ruleWithRecipients?.recipients || [])
    return Array.from(new Set(allRecipients.map(recipient => recipient.userId)))
  }, [alertRulesLoaded])

  useEffect(() => {
    setUniqueEmailRecipients(uniqueEmailRecipientsFromLoaded)
  }, [uniqueEmailRecipientsFromLoaded])

  const handleNewAlertMenuClick: MenuProps['onClick'] = useCallback(
    menuItem => {
      const key = menuItem.key
      setAlertRules(prev => ({
        ...prev,
        [key]: _.cloneDeep(DEFAULT_ALERT_PROPS[key])
      }))
      setActiveKey(prev => [...prev, key])
    },
    []
  )

  const newAlertMenuProps = useMemo(() => {
    const addAlertMenuItems: MenuProps['items'] = [
      {
        label: ALERT_RULE_TITLE_MAPPER['hvoFuel'],
        key: 'hvoFuel',
        disabled: alertRules['hvoFuel'] ? true : false
      },
      {
        label: ALERT_RULE_TITLE_MAPPER['logisticsWindow'],
        key: 'logisticsWindow',
        disabled: alertRules['logisticsWindow'] ? true : false
      },
      {
        label: ALERT_RULE_TITLE_MAPPER['materialThresholds'],
        key: 'materialThresholds',
        disabled: alertRules['materialThresholds'] ? true : false
      },
      {
        label: ALERT_RULE_TITLE_MAPPER['materialWatchlist'],
        key: 'materialWatchlist',
        disabled: alertRules['materialWatchlist'] ? true : false
      },
      {
        label: ALERT_RULE_TITLE_MAPPER['supplierWatchlist'],
        key: 'supplierWatchlist',
        disabled: alertRules['supplierWatchlist'] ? true : false
      },
      {
        label: ALERT_RULE_TITLE_MAPPER['materialCertifications'],
        key: 'materialCertifications',
        disabled: alertRules['materialCertifications'] ? true : false
      },
      {
        label: ALERT_RULE_TITLE_MAPPER['wasteCompliance'],
        key: 'wasteCompliance',
        disabled: alertRules['wasteCompliance'] ? true : false
      },
      {
        label: <Tooltip title="Coming soon">Project Suppliers</Tooltip>,
        key: 'projectSuppliers',
        disabled: true
      }
    ]

    const filteredAlertItems = addAlertMenuItems.filter(
      item => item && typeof item.key === 'string' && !alertRules[item.key]
    )

    const groupedFilteredAlertMenuItems: MenuProps['items'] = []
    for (const group of ALERT_RULE_GROUPS) {
      const groupedItems = filteredAlertItems.filter(
        item =>
          item &&
          typeof item.key === 'string' &&
          (ALERT_RULE_GROUPING[item.key] ?? 'Quality Assurance') === group
      )

      if (groupedItems.length > 0) {
        groupedFilteredAlertMenuItems.push({
          key: group,
          type: 'group',
          label: group,
          children: groupedItems
        })
      }
    }

    return {
      items: groupedFilteredAlertMenuItems,
      onClick: handleNewAlertMenuClick
    }
  }, [alertRules, handleNewAlertMenuClick])

  // Memoize filtered alert rules to avoid recomputing
  const filteredAlertRulesEntries = useMemo(
    () =>
      Object.entries(alertRules).filter(
        ([key, rule]) =>
          rule != null && ALERT_RULE_TITLE_MAPPER[key] !== undefined
      ),
    [alertRules]
  )

  const numOfActiveAlert = useMemo(
    () => filteredAlertRulesEntries.length,
    [filteredAlertRulesEntries]
  )

  const numOfEnabledAlerts = useMemo(
    () =>
      filteredAlertRulesEntries.reduce(
        (count: number, [key, rule]: any) =>
          count + determineAlertRuleCount(key, rule),
        0
      ),
    [filteredAlertRulesEntries]
  )

  const copyAlertsMenuClick: MenuProps['onClick'] = useCallback(
    menuItem => {
      const copyFromProjectId = menuItem.key

      // Use numOfActiveAlert which is already memoized
      if (numOfActiveAlert > 0) {
        Modal.confirm({
          title: 'Overwrite existing alerts?',
          content:
            'Copying alerts will overwrite your existing alert settings. This action cannot be undone. Are you sure you want to continue?',
          okText: 'Yes',
          okType: 'danger',
          cancelText: 'No',
          onOk: () => {
            copyAlertsFrom(copyFromProjectId)
          }
        })
      } else {
        copyAlertsFrom(copyFromProjectId)
      }
    },
    [numOfActiveAlert, copyAlertsFrom]
  )

  const copyFromAvailableProjectsList = useMemo(() => {
    // First check current user has Account Admin access - if so, they can copy from any project, so we check for projects with at least 1 alert

    return {
      items: filteredProjects
        ? filteredProjects.map(otherProject => {
            const projectId = otherProject.projectId as string
            const projectAlertData = alertUsageByProject?.[projectId]
            const usage = projectAlertData?.count ?? 0

            // Collect all recipients from this project's alert rules
            let hasInaccessibleRecipients = false
            if (projectAlertData?.data) {
              const allRecipients = Object.values(
                projectAlertData.data as Record<string, RuleWithRecipientsType>
              ).flatMap(
                ruleWithRecipients => ruleWithRecipients?.recipients || []
              )

              // Check if any recipient doesn't have access to current project
              hasInaccessibleRecipients = allRecipients.some(
                recipient => !currentProjectUserIdSet.has(recipient.userId)
              )
            }

            // Determine tooltip message and disabled state
            const TooltipMessage =
              usage === 0
                ? 'No alerts to copy from this project.'
                : usage > project.maxAlertRules
                  ? `This project has ${usage} alerts, which exceeds your current project's alert limit of ${project.maxAlertRules}.`
                  : hasInaccessibleRecipients
                    ? 'This project has alert rules with email recipients who do not have access to the current project.'
                    : null

            return {
              label: (
                <Tooltip
                  title={TooltipMessage}
                >{`${otherProject.name} (${usage})`}</Tooltip>
              ),
              key: projectId,
              disabled: !!TooltipMessage
            }
          })
        : [],
      onClick: copyAlertsMenuClick
    }
  }, [
    filteredProjects,
    copyAlertsMenuClick,
    alertUsageByProject,
    currentProjectUserIdSet,
    project.maxAlertRules
  ])

  const copyToAvailableProjectsList = useMemo(() => {
    return {
      items: filteredProjects
        ? filteredProjects.map(otherProject => {
            const projectId = otherProject.projectId as string
            const projectMaxAlertRules = otherProject.maxAlertRules ?? 0

            let hasInaccessibleRecipients = false
            if (uniqueEmailRecipients.length > 0 && usersAccountMappings) {
              hasInaccessibleRecipients = uniqueEmailRecipients.some(
                recipientId => {
                  // With key accountId_userId in usersAccountMappings, check if the recipientId has access to the otherProject
                  const userAccount =
                    usersAccountMappings[`${accountId}_${recipientId}`]
                  return !userAccount?.projectPermissions?.some(
                    projectPermission =>
                      projectPermission.projectId === otherProject.projectId
                  )
                }
              )
            }

            // Determine tooltip message and disabled state
            const TooltipMessage =
              numOfEnabledAlerts > projectMaxAlertRules
                ? `The current project has ${numOfEnabledAlerts} alerts, which exceeds this project's alert limit of ${projectMaxAlertRules}.`
                : hasInaccessibleRecipients
                  ? 'The current project has alert rules with email recipients who do not have access to this project.'
                  : null

            return {
              label: (
                <Tooltip title={TooltipMessage}>{otherProject.name}</Tooltip>
              ),
              key: projectId,
              disabled: !!TooltipMessage,
              status: otherProject.status
            }
          })
        : [],
      onClick: copyAlertsMenuClick
    }
  }, [
    filteredProjects,
    copyAlertsMenuClick,
    numOfEnabledAlerts,
    uniqueEmailRecipients,
    usersAccountMappings,
    accountId
  ])

  const handleAlertRuleDelete = useCallback(
    (key: string) => {
      Modal.confirm({
        title: 'Delete alert',
        content: 'This action cannot be undone. Are you sure?',
        autoFocusButton: null,
        okText: 'Yes',
        okType: 'danger',
        cancelText: 'No',
        onOk: () => {
          dispatch(
            trackMixpanel('frontend_alerts_delete_click', {
              account_id: accountId,
              project_id: project.projectId,
              alert_rule: key
            })
          )

          // Remove the alert rule from state to immediately reflect the change in the UI
          setAlertRules(prev => ({ ...prev, [key]: null }))

          // Check that alertRulesLoaded exists and has the key before trying to delete
          if (accountId && alertRulesLoaded[key]) {
            const updatedAlertRulesLoaded = { ...alertRulesLoaded, [key]: null }
            dispatch(
              editAlertRules(
                accountId,
                project.projectId,
                updatedAlertRulesLoaded,
                () => {}, // Don't reload after delete - we already updated the UI optimistically, and reloading would cause a flash of the deleted alert rule before it disappears
                showSuccessNotification,
                showFailureNotification
              )
            )
            setAlertRulesLoaded(updatedAlertRulesLoaded) // Update local loaded state to reflect deletion, so that if user tries to reset after deleting, it doesn't bring back the deleted alert rule
          }
        }
      })
    },
    [
      dispatch,
      accountId,
      project.projectId,
      alertRulesLoaded,
      showSuccessNotification,
      showFailureNotification
    ]
  )

  const alertRuleItems: CollapseProps['items'] = useMemo(
    () =>
      filteredAlertRulesEntries
        .map(([key]) => {
          return {
            key: key,
            label: (
              <AlertRuleTitleBar>
                <AlertRuleTitle>{ALERT_RULE_TITLE_MAPPER[key]}</AlertRuleTitle>
                <AlertRuleButtons className="hide-if-closed">
                  <Tooltip title="Reset to saved values">
                    <ColourButton.BasicBorderless
                      id={`ResetButton-${key}`}
                      aria-label="Reset alert"
                      style={{ padding: '4px 9px', borderRadius: '50%' }}
                      // Compare against originally loaded values
                      disabled={_.isEqual(
                        alertRules[key],
                        alertRulesLoaded &&
                          Object.prototype.hasOwnProperty.call(
                            alertRulesLoaded,
                            key
                          )
                          ? alertRulesLoaded[key]
                          : {}
                      )}
                      onClick={e => {
                        e.preventDefault()
                        e.stopPropagation()
                        // Compare against originally loaded values - use functional update
                        setAlertRules(prev => ({
                          ...prev,
                          [key]: {
                            ...(alertRulesLoaded &&
                            Object.prototype.hasOwnProperty.call(
                              alertRulesLoaded,
                              key
                            )
                              ? alertRulesLoaded[key]
                              : {})
                          }
                        }))
                      }}
                    >
                      <icons.Restore
                        colour="currentColor"
                        size={sizes.X_SMALL}
                      />
                    </ColourButton.BasicBorderless>
                  </Tooltip>
                  <Tooltip title="Remove alert">
                    <ColourButton.BasicBorderless
                      id={`RemoveButton-${key}`}
                      aria-label="Remove alert"
                      style={{ padding: '4px 9px', borderRadius: '50%' }}
                      onClick={e => {
                        e.preventDefault()
                        e.stopPropagation()
                        handleAlertRuleDelete(key)
                      }}
                    >
                      <icons.TrashCan colour={colours.ERROR} />
                    </ColourButton.BasicBorderless>
                  </Tooltip>
                </AlertRuleButtons>
                {!activeKey.includes(key) && (
                  <AlertRuleStatus>
                    {alertRules[key].isEnabled ? (
                      <StatusActive>
                        <icons.CircleTicked colour={'currentColor'} />
                        <span>Enabled</span>
                      </StatusActive>
                    ) : (
                      <StatusInactive>
                        <icons.CircleCrossed
                          colour={'currentColor'}
                          size={sizes.MEDIUM}
                        />
                        <span>Disabled</span>
                      </StatusInactive>
                    )}
                  </AlertRuleStatus>
                )}
              </AlertRuleTitleBar>
            ),
            children: (
              <AlertRule
                projectId={project.projectId}
                alertKey={key}
                alertRules={alertRules}
                setAlertRules={setAlertRules}
                cannotEnable={numOfEnabledAlerts === project.maxAlertRules}
              />
            ),
            forceRender: true,
            tag: ALERT_RULE_TITLE_MAPPER[key]
          }
        })
        .sort((a, b) => a.tag.localeCompare(b.tag)),
    [
      filteredAlertRulesEntries,
      alertRules,
      alertRulesLoaded,
      activeKey,
      project.projectId,
      project.maxAlertRules,
      numOfEnabledAlerts,
      handleAlertRuleDelete
    ]
  )

  const handleAlertRuleSave = useCallback(() => {
    Modal.confirm({
      title: 'Save alert',
      content: (
        <>
          <p>
            Changes will only be applied to new records. Alerts raised for
            existing records will be unaffected by this change.
          </p>
          <p>Click OK to continue.</p>
        </>
      ),
      onOk: () => {
        const onlyUpdatedAlertRules: string[] = []
        Object.keys(alertRules).forEach(key => {
          if (!_.isEqual(alertRules[key], loadedAlertRules.data[key])) {
            onlyUpdatedAlertRules.push(key)
          }
        })

        // enabled alerts array for mixpanel
        const enabledAlertsForMixpanel = Object.entries(alertRules)
          .filter(
            ([key, rule]: [string, any]) =>
              rule != null &&
              ALERT_RULE_TITLE_MAPPER[key] !== undefined &&
              rule.isEnabled
          )
          .map(([key, rule]: [string, any]) => key)

        dispatch(
          trackMixpanel('frontend_alerts_save_click', {
            account_id: accountId,
            project_id: project.projectId,
            alert_rules: onlyUpdatedAlertRules,
            enabled_alerts: enabledAlertsForMixpanel
          })
        )

        // Dispatch additional Mixpanel event if no. of enabled alerts has reached the max alert limit
        if (numOfEnabledAlerts === project.maxAlertRules) {
          dispatch(
            trackMixpanel('frontend_alerts_limit_reached', {
              account_id: accountId,
              project_id: project.projectId,
              max_alert_rules: project.maxAlertRules
            })
          )
        }

        if (accountId) {
          dispatch(
            editAlertRules(
              accountId,
              project.projectId,
              alertRules,
              reload,
              showSuccessNotification,
              showFailureNotification
            )
          )
        }
      }
    })
  }, [
    alertRules,
    dispatch,
    accountId,
    project.projectId,
    project.maxAlertRules,
    numOfEnabledAlerts,
    loadedAlertRules.data,
    reload,
    showSuccessNotification,
    showFailureNotification
  ])

  const { saveDisabled, saveDisabledTooltipText } = useMemo(() => {
    if (numOfEnabledAlerts > project.maxAlertRules) {
      return {
        saveDisabled: true,
        saveDisabledTooltipText:
          "You've reached your alert limit. Contact our team to upgrade and enable more alerts."
      }
    }
    if (
      alertRules['materialThresholds'] &&
      alertRules['materialThresholds'].isEnabled &&
      alertRules['materialThresholds'].thresholds?.length === 0
    ) {
      return {
        saveDisabled: true,
        saveDisabledTooltipText:
          'Please add at least one threshold to the Material Thresholds'
      }
    }
    if (
      alertRules['materialWatchlist'] &&
      alertRules['materialWatchlist'].isEnabled &&
      alertRules['materialWatchlist'].watchedMaterials?.length === 0
    ) {
      return {
        saveDisabled: true,
        saveDisabledTooltipText:
          'Please add at least one keyword to the Material Watchlist'
      }
    }
    if (
      alertRules['supplierWatchlist'] &&
      alertRules['supplierWatchlist'].isEnabled &&
      alertRules['supplierWatchlist'].watchedSuppliers?.length === 0
    ) {
      return {
        saveDisabled: true,
        saveDisabledTooltipText:
          'Please add at least one supplier to the Supplier Watchlist'
      }
    }
    if (loadedAlertRules.loading || loadedAlertRules.error) {
      return {
        saveDisabled: true,
        saveDisabledTooltipText: 'Loading Alert, please wait...'
      }
    }

    if (_.isEqual(alertRules, alertRulesLoaded)) {
      return {
        saveDisabled: true,
        saveDisabledTooltipText: 'No changes made to these Alerts.'
      }
    }
    return { saveDisabled: false, saveDisabledTooltipText: '' }
  }, [
    alertRules,
    alertRulesLoaded,
    loadedAlertRules.error,
    loadedAlertRules.loading,
    numOfEnabledAlerts,
    project.maxAlertRules
  ])

  const COPY_FROM_TOOLTIP_TEXT = 'Copy alert settings from another project'
  const COPY_TO_TOOLTIP_TEXT = !saveDisabled
    ? 'Please save changes before copying alert settings'
    : 'Copy alert settings to other projects'

  // Check for unsaved changes
  const hasUnsavedChanges = useMemo(() => {
    return !_.isEqual(alertRules, alertRulesLoaded)
  }, [alertRules, alertRulesLoaded])

  const location = useLocation()
  const navigator = useContext(UNSAFE_NavigationContext).navigator

  // Notify parent component of unsaved changes state
  useEffect(() => {
    if (onHasUnsavedChanges) {
      onHasUnsavedChanges(hasUnsavedChanges)
    }
  }, [hasUnsavedChanges, onHasUnsavedChanges])

  // Use custom hook to warn about unsaved changes
  useUnsavedChangesWarning(hasUnsavedChanges, navigator, location)

  if (loading) {
    return <Spinner />
  }

  if (error) {
    return <span>An error has occurred</span>
  }

  const infiniteAlertsSentinel = 255

  return (
    <ProjectsData id="alert-project-data">
      <TitleBar>
        <h3>{project.name}</h3>
        <RightSide>
          {numOfActiveAlert > 0 && (
            <>
              <AlertUsage>
                <AlertUsageText>
                  <IconAndText>
                    <icons.ShieldTick
                      size={sizes.SMALL_MEDIUM}
                      colour="currentColor"
                    />
                    <Text size={sizes.X_SMALL}>Active alerts</Text>
                  </IconAndText>
                  <Text size={sizes.X_SMALL}>
                    {numOfEnabledAlerts} /{' '}
                    {project.maxAlertRules !== infiniteAlertsSentinel ? (
                      project.maxAlertRules
                    ) : (
                      <Infinite>&infin;</Infinite>
                    )}
                  </Text>
                </AlertUsageText>
                {project.maxAlertRules !== infiniteAlertsSentinel && (
                  <Progress
                    percent={(numOfEnabledAlerts / project.maxAlertRules) * 100}
                    showInfo={false}
                    strokeColor={
                      numOfEnabledAlerts > project.maxAlertRules
                        ? colours.RED
                        : colours.TEAL
                    }
                    trailColor={colours.CONTROL_PANEL_GREY}
                    style={{ width: '100%', lineHeight: '1.25' }}
                    strokeLinecap="round"
                  />
                )}
              </AlertUsage>
              <Dropdown
                menu={newAlertMenuProps}
                trigger={['click']}
                disabled={numOfEnabledAlerts >= project.maxAlertRules}
              >
                <ColourButton.Trigger style={{ margin: '0' }}>
                  <icons.Plus size={sizes.SMALL} />
                  <Spacer.Tiny />
                  Add new alert
                  <Spacer.Tiny />
                  <icons.ChevronDown />
                </ColourButton.Trigger>
              </Dropdown>
              {copyFromAvailableProjectsList.items.length > 0 &&
                userIsAccountAdmin && (
                  <>
                    <Tooltip title={COPY_FROM_TOOLTIP_TEXT}>
                      <Dropdown
                        menu={copyFromAvailableProjectsList}
                        trigger={['click']}
                      >
                        <ColourButton.Trigger style={{ margin: '0' }}>
                          Copy from...
                          <Spacer.Tiny />
                          <icons.ChevronDown />
                        </ColourButton.Trigger>
                      </Dropdown>
                    </Tooltip>
                    <Tooltip title={COPY_TO_TOOLTIP_TEXT}>
                      <ColourButton.Trigger
                        style={{ margin: '0' }}
                        disabled={!saveDisabled}
                        onClick={() => setCopyToModalOpen(true)}
                      >
                        Copy to...
                      </ColourButton.Trigger>
                    </Tooltip>
                  </>
                )}
              <Tooltip title={saveDisabledTooltipText}>
                <ColourButton.Action
                  // Compare against originally loaded values
                  disabled={saveDisabled}
                  onClick={e => {
                    e.preventDefault()
                    e.stopPropagation()
                    handleAlertRuleSave()
                  }}
                >
                  Save
                </ColourButton.Action>
              </Tooltip>
            </>
          )}
        </RightSide>
      </TitleBar>

      {/* No alerts set up yet */}
      {numOfActiveAlert === 0 && (
        <>
          <Section style={{ textAlign: 'center' }}>
            <h4>No alerts set up yet</h4>
            <DataWrapper>
              You're not receiving any alerts at the moment.
              <br />
              Set up custom notifications to stay informed and in control of
              what matters on your project.
            </DataWrapper>
            <DataWrapper>
              <ButtonWrapper>
                <Dropdown menu={newAlertMenuProps} trigger={['click']}>
                  <ColourButton.Trigger style={{ margin: '0' }}>
                    <icons.Plus size={sizes.SMALL} />
                    <Spacer.Tiny />
                    Add new alert
                    <Spacer.Tiny />
                    <icons.ChevronDown />
                  </ColourButton.Trigger>
                </Dropdown>
                {copyFromAvailableProjectsList.items.length > 0 &&
                  userIsAccountAdmin && (
                    <Tooltip title={COPY_FROM_TOOLTIP_TEXT}>
                      <Dropdown
                        menu={copyFromAvailableProjectsList}
                        trigger={['click']}
                      >
                        <ColourButton.Trigger style={{ margin: '0' }}>
                          Copy from...
                          <Spacer.Tiny />
                          <icons.ChevronDown />
                        </ColourButton.Trigger>
                      </Dropdown>
                    </Tooltip>
                  )}
              </ButtonWrapper>
            </DataWrapper>
          </Section>
          <Section style={{ padding: '1.5rem 33.33%' }}>
            <IntroducingAdvancedAlerts />
          </Section>
        </>
      )}
      {/* Alerts set up */}
      {numOfActiveAlert > 0 && (
        <Collapse
          items={alertRuleItems}
          expandIcon={({ isActive }) => (
            <icons.ChevronDown size={'10'} rotate={isActive ? 180 : 0} />
          )}
          activeKey={activeKey}
          onChange={activeKey => {
            setActiveKey(activeKey as string[])
          }}
        />
      )}

      <AlertSettingsCopyToModal
        open={copyToModalOpen}
        availableProjects={copyToAvailableProjectsList.items}
        onClose={() => setCopyToModalOpen(false)}
        onCopyTo={copyAlertsTo}
      />
    </ProjectsData>
  )
}

const IntroducingAdvancedAlerts = () => {
  return (
    <div
      style={{
        position: 'relative',
        paddingBottom: '56.25%',
        height: '0'
      }}
    >
      <iframe
        title="Advanced Alerts Video"
        style={{
          position: 'absolute',
          width: '100%',
          height: '100%'
        }}
        src="https://www.tella.tv/video/advanced-alerts-streamlining-project-monitoring-gbtq/embed?b=0&title=0&a=0&loop=0&t=0&muted=0&wt=0"
        allowFullScreen
      ></iframe>
    </div>
  )
}

type AlertSettingsProps = {
  project: ProjectSettings
  onHasUnsavedChanges?: (hasUnsavedChanges: boolean) => void
}

export interface ProjectSettings {
  name: string
  projectId: string
  maxAlertRules: number
  isOneClickLcaEnabled: boolean
  isEC3Enabled: boolean
}
type ProjectUser = {
  userId: string
  projectPermissions: any[]
}
type RecipientType = {
  userId: string
}
type RuleWithRecipientsType = {
  alertKey: string
  recipients: RecipientType[]
}

const ProjectsData = styled.div`
  flex-grow: 1;
  position: relative;
  background-color: ${colours.WHITE};
  color: ${colours.CORE_NAVY};
  border-radius: 0.625rem;
  padding: 1rem 1.5rem 1rem 1.5rem;
  box-shadow: 0px 1px 3px 0px #00000026;
`
const TitleBar = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 0.875rem;
  min-height: 2rem;

  & > h3 {
    color: ${colours.CORE_NAVY};
    font-family: "Jakarta Sans", "Inter", sans-serif !important;
    font-size: 1.125rem;
    font-weight: 700;
    margin: 0;
`
const Section = styled.div`
  background-color: ${colours.CONTROL_PANEL_GREY};
  border-radius: 0.625rem;
  padding: 1rem 1.5rem;
  margin-bottom: 0.625rem;

  & > h3 {
    color: ${colours.CORE_NAVY};
    font-family: 'Jakarta Sans', 'Inter', sans-serif !important;
    font-size: 1.5rem;
    font-weight: 700;
    margin-bottom: 0.25rem;
  }

  & > h4 {
    color: ${colours.CORE_NAVY};
    font-family: 'Jakarta Sans', 'Inter', sans-serif !important;
    font-size: 1rem;
    font-weight: 700;
    margin-bottom: 0.25rem;
  }
`
const RightSide = styled.div`
  display: flex;
  align-items: center;
  gap: 0.625rem;
`
const AlertUsage = styled.div`
  display: flex;
  flex-direction: column;
  width: 10rem;
  max-height: 2rem;
  margin-right: 0.625rem;
  justify-content: space-between;
`
const AlertUsageText = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  margin-top: 0.125rem;
`
const IconAndText = styled.div`
  display: flex;
  align-items: center;
  gap: 0.25rem;
`
const DataWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.625rem 0;
`
const AlertRuleTitleBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 2rem;
`
const AlertRuleTitle = styled.div`
  font-size: 0.9375rem;
  font-weight: 600;
  color: ${colours.CORE_NAVY};
`
const AlertRuleButtons = styled.div`
  display: flex;
  align-items: center;
  gap: 0.25rem;
`
const AlertRuleStatus = styled.div`
  font-size: 16px;
`
const Infinite = styled.span`
  font-size: 0.875rem;
`
const StatusActive = styled.div`
  display: flex;
  align-items: center;
  gap: 0.25rem;
  font-size: 0.875rem;
  font-weight: 400;
  color: ${colours.GREEN};
  width: 5rem;
`
const StatusInactive = styled.div`
  display: flex;
  align-items: center;
  gap: 0.25rem;
  font-size: 0.875rem;
  font-weight: 400;
  color: ${colours.ERROR};
  width: 5rem;
`
const ButtonWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 1rem;
`
